import {ProductionMaterialItem, ProductionQueueItem, ProductionTrackedEntity} from "../pages/modules/production/types.ts";

export type MaterialTotals = Record<string, number>;

export type MaterialsCatalogs = {
    ships: Record<string, {materials?: Record<string, number>}>;
    vehicles: Record<string, {materials?: Record<string, number>}>;
    items: Record<string, {materials?: Record<string, number>}>;
    droids: Record<string, {materials?: Record<string, number>}>;
};

export type MaterialAccessEntity = Pick<ProductionTrackedEntity, "entityId" | "entityType" | "location">;

function indexById<T extends {id: string}>(rows: T[]): Record<string, T> {
    return rows.reduce((acc, row) => {
        if (row?.id) {
            acc[row.id] = row;
        }
        return acc;
    }, {} as Record<string, T>);
}

export function buildMaterialsCatalogs(raw: {
    ships: Array<{id: string; materials?: Record<string, number>}>;
    vehicles: Array<{id: string; materials?: Record<string, number>}>;
    items: Array<{id: string; materials?: Record<string, number>}>;
    droids: Array<{id: string; materials?: Record<string, number>}>;
}): MaterialsCatalogs {
    return {
        ships: indexById(raw.ships),
        vehicles: indexById(raw.vehicles),
        items: indexById(raw.items),
        droids: indexById(raw.droids),
    };
}

function getLocationString(source: Record<string, unknown> | undefined, key: string): string {
    if (!source) {
        return "";
    }
    const value = source[key];
    if (typeof value === "string") {
        return value.trim();
    }
    if (typeof value === "number") {
        return String(value);
    }
    return "";
}

function getLocationNumber(source: Record<string, unknown> | undefined, key: string): number | null {
    if (!source) {
        return null;
    }
    const value = source[key];
    if (typeof value === "number" && Number.isFinite(value)) {
        return value;
    }
    if (typeof value === "string") {
        const parsed = Number(value);
        if (Number.isFinite(parsed)) {
            return parsed;
        }
    }
    return null;
}

function parseTypeFromHref(href?: string): "ships" | "vehicles" | "items" | "droids" | "weapons" | null {
    if (!href) {
        return null;
    }
    const match = /\/types\/([^/]+)\//.exec(href);
    if (!match) {
        return null;
    }
    const type = match[1]?.toLowerCase();
    if (type === "ships" || type === "vehicles" || type === "items" || type === "droids" || type === "weapons") {
        return type;
    }
    return null;
}

function parseTypeFromEntityId(entityId: string): "ships" | "vehicles" | "items" | "droids" | null {
    const prefix = entityId.split(":")[0];
    if (prefix === "2") return "ships";
    if (prefix === "3") return "vehicles";
    if (prefix === "14") return "items";
    if (prefix === "16") return "droids";
    return null;
}

function parseTypeFromEntityType(rawType?: string): "ships" | "vehicles" | "items" | "droids" | "weapons" | null {
    if (!rawType) {
        return null;
    }
    const type = rawType.trim().toLowerCase();
    if (type === "ship" || type === "ships") return "ships";
    if (type === "vehicle" || type === "vehicles") return "vehicles";
    if (type === "item" || type === "items") return "items";
    if (type === "droid" || type === "droids") return "droids";
    if (type === "weapon" || type === "weapons") return "weapons";
    return null;
}

function resolveQueueItemType(item: ProductionQueueItem): "ships" | "vehicles" | "items" | "droids" | "weapons" | null {
    return (
        parseTypeFromEntityType(item.entityType)
        ?? parseTypeFromHref(item.entityHref)
        ?? parseTypeFromEntityId(item.entityId)
    );
}

export function buildRequiredMaterialsForQueue(
    items: ProductionQueueItem[],
    catalogs: MaterialsCatalogs
): {materials: MaterialTotals} {
    const totals: MaterialTotals = {};

    items.forEach((item) => {
        const quantity = Math.max(1, Number(item.quantity || 1));
        const targetType = resolveQueueItemType(item);
        if (!targetType || targetType === "weapons") {
            return;
        }

        const entry = catalogs[targetType][item.entityId];
        const materials = entry?.materials ?? null;
        if (!materials || Object.keys(materials).length === 0) {
            return;
        }

        Object.entries(materials).forEach(([materialId, value]) => {
            const base = Number(value || 0);
            if (base <= 0) {
                return;
            }
            totals[materialId] = (totals[materialId] ?? 0) + base * quantity;
        });
    });

    return {materials: totals};
}

function getMaterialLocation(material: ProductionMaterialItem): Record<string, unknown> | undefined {
    const location = material.location;
    if (!location || typeof location !== "object") {
        return undefined;
    }
    return location as Record<string, unknown>;
}

function getEntityLocation(entity: MaterialAccessEntity): Record<string, unknown> | undefined {
    const location = entity.location;
    if (!location || typeof location !== "object") {
        return undefined;
    }
    return location as Record<string, unknown>;
}

function isStationMaterialAccessible(entity: MaterialAccessEntity, material: ProductionMaterialItem): boolean {
    const entityId = entity.entityId ?? "";
    const entityLocation = getEntityLocation(entity);
    const materialLocation = getMaterialLocation(material);
    const materialContainerId = getLocationString(materialLocation, "containerId");

    if (entityId && materialContainerId === entityId) {
        return true;
    }

    const containerType = getLocationString(materialLocation, "containerType").toLowerCase();
    if (containerType !== "station") {
        return false;
    }

    const systemId = getLocationString(entityLocation, "systemId");
    if (!systemId || systemId !== getLocationString(materialLocation, "systemId")) {
        return false;
    }

    const entityX = getLocationNumber(entityLocation, "sysX");
    const entityY = getLocationNumber(entityLocation, "sysY");
    const materialX = getLocationNumber(materialLocation, "systemX");
    const materialY = getLocationNumber(materialLocation, "systemY");

    if (entityX === null || entityY === null || materialX === null || materialY === null) {
        return false;
    }

    return Math.abs(materialX - entityX) <= 1 && Math.abs(materialY - entityY) <= 1;
}

function isFacilityMaterialAccessible(entity: MaterialAccessEntity, material: ProductionMaterialItem): boolean {
    const entityId = entity.entityId ?? "";
    const entityLocation = getEntityLocation(entity);
    const materialLocation = getMaterialLocation(material);
    const materialContainerId = getLocationString(materialLocation, "containerId");

    if (entityId && materialContainerId === entityId) {
        return true;
    }

    const containerType = getLocationString(materialLocation, "containerType").toLowerCase();
    if (containerType !== "facility") {
        return false;
    }

    const cityId = getLocationString(entityLocation, "cityId");
    if (!cityId || cityId !== getLocationString(materialLocation, "cityId")) {
        return false;
    }

    return true;
}

export function isMaterialAccessible(entity: MaterialAccessEntity, material: ProductionMaterialItem): boolean {
    if (entity.entityType === "station") {
        return isStationMaterialAccessible(entity, material);
    }
    if (entity.entityType === "facility") {
        return isFacilityMaterialAccessible(entity, material);
    }
    return false;
}

export function buildAccessibleMaterialTotals(
    entity: MaterialAccessEntity,
    materials: ProductionMaterialItem[]
): MaterialTotals {
    const totals: MaterialTotals = {};

    materials.forEach((material) => {
        if (!isMaterialAccessible(entity, material)) {
            return;
        }
        const materialId = String(material.materialTypeId || "").trim();
        if (!materialId) {
            return;
        }
        const quantity = Number(material.quantity || 0);
        if (!Number.isFinite(quantity) || quantity <= 0) {
            return;
        }
        totals[materialId] = (totals[materialId] ?? 0) + quantity;
    });

    return totals;
}

export function buildAccessGroupKey(entity: MaterialAccessEntity): string {
    const entityLocation = getEntityLocation(entity);
    if (entity.entityType === "facility") {
        const cityId = getLocationString(entityLocation, "cityId");
        if (cityId) {
            return `facility:city:${cityId}`;
        }
        return `facility:entity:${entity.entityId}`;
    }
    if (entity.entityType === "station") {
        const systemId = getLocationString(entityLocation, "systemId");
        const sysX = getLocationNumber(entityLocation, "sysX");
        const sysY = getLocationNumber(entityLocation, "sysY");
        if (systemId && sysX !== null && sysY !== null) {
            return `station:${systemId}:${sysX}:${sysY}`;
        }
        return `station:entity:${entity.entityId}`;
    }
    return `entity:${entity.entityId}`;
}

export function addMaterialTotals(target: MaterialTotals, additions: MaterialTotals): MaterialTotals {
    Object.entries(additions).forEach(([materialId, quantity]) => {
        if (!Number.isFinite(quantity) || quantity <= 0) {
            return;
        }
        target[materialId] = (target[materialId] ?? 0) + quantity;
    });
    return target;
}
